import { useState, useMemo, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { Flex, Input, Radio, IconButton, Icon, icon, Tooltip } from '@nula/core-ui';
import { EMOJI_GROUPS } from '@/lib/emojis/emoji-manifest';
import { EmojiRenderer } from '@/components/emoji-renderer/emoji-renderer';
import { emojiSpriteUrl } from '@/lib/emojis/emoji-url';
import type { EmojiRef, EmojiVariant } from '@/components/emoji-renderer/types';
import { useLaunchAnimation } from '@/components/popover-launch/use-launch-animation';
import styles from './emoji-picker.module.css';

const GROUP_ICONS: Record<string, string> = {
  smileys:             'sentiment_satisfied',
  symbols:             'stars',
  nature:              'psychiatry',
  'food-and-drinks':   'restaurant',
  'travel-and-places': 'flight',
  animals:             'pets',
  gestures:            'waving_hand',
  objects:             'lightbulb',
  activity:            'sports_soccer',
  people:              'person',
  buildings:           'apartment',
};

interface EmojiPickerProps {
  onSelect: (ref: EmojiRef) => void;
  defaultVariant: EmojiVariant;
  showVariantToggle: boolean;
  /** When true, hides the internal search bar (use when the parent provides search externally) */
  hideSearch?: boolean;
  /** External search query — overrides internal query when provided */
  externalQuery?: string;
  /** When true, picker fills its flex container (for use inside ToolPanel) */
  fillContainer?: boolean;
}

// Module-level cache so sprites loaded in one picker open are instant on next open.
const loadedSprites = new Set<string>();

function useSpriteReady(group: string): boolean {
  const url = group ? emojiSpriteUrl(group) : '';
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!url) { setReady(true); return; }
    if (loadedSprites.has(url)) { setReady(true); return; }
    setReady(false);
    const img = new Image();
    img.onload = () => { loadedSprites.add(url); setReady(true); };
    img.onerror = () => setReady(true);
    img.src = url;
  }, [url]);

  return ready;
}

function preloadSprite(group: string) {
  const url = emojiSpriteUrl(group);
  if (loadedSprites.has(url)) return;
  const img = new Image();
  img.onload = () => loadedSprites.add(url);
  img.src = url;
}

interface EmojiPreviewGridProps {
  onSelect: (ref: EmojiRef) => void;
  variant?: EmojiVariant;
  /** Number of emojis to show; defaults to 32 (4 rows × 8 cols) */
  count?: number;
}

/** 3-row grid of emoji previews — used in the "All" overview tab. */
export function EmojiPreviewGrid({ onSelect, variant = 'stamp', count = 32 }: EmojiPreviewGridProps) {
  const group = EMOJI_GROUPS[0];
  const spriteReady = useSpriteReady(group.id);
  const { launchingId, launch } = useLaunchAnimation();

  // Preload the first sprite on mount
  useEffect(() => { preloadSprite(group.id); }, [group.id]);

  // Randomise once on mount — shuffled order stays stable while the panel is open.
  const emojis = useMemo(() => {
    const all = group.emojis.map(e => ({ ...e, group: group.id }));
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }
    return all.slice(0, count);
  }, [group, count]);

  if (!spriteReady) {
    return (
      <div className={styles.previewGrid}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className={styles.skeletonCell} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.previewGrid}>
      {emojis.map(emoji => {
        const eid = `${emoji.group}-${emoji.id}`;
        return (
          <IconButton
            key={eid}
            transparent
            aria-label={emoji.name}
            className={styles.emojiBtn}
            data-launching={launchingId === eid || undefined}
            onClick={() => launch(eid, () => onSelect({ group: emoji.group, id: emoji.id, variant }))}
          >
            <EmojiRenderer group={emoji.group} id={emoji.id} size={28} variant={variant} alt={emoji.name} useSprite />
          </IconButton>
        );
      })}
    </div>
  );
}

export function EmojiPicker({ onSelect, defaultVariant, showVariantToggle, hideSearch = false, externalQuery, fillContainer = false }: EmojiPickerProps) {
  const [activeGroupId, setActiveGroupId] = useState(EMOJI_GROUPS[0].id);
  const [internalQuery, setInternalQuery] = useState('');
  const query = externalQuery ?? internalQuery;
  const [variant, setVariant] = useState<EmojiVariant>(defaultVariant);
  const preloadedRef = useRef(new Set<string>());
  const { launchingId, launch } = useLaunchAnimation();

  // Preload first group immediately, then remaining groups after a short delay.
  useEffect(() => {
    preloadedRef.current.add(EMOJI_GROUPS[0].id);
    preloadSprite(EMOJI_GROUPS[0].id);
    const t = setTimeout(() => {
      EMOJI_GROUPS.slice(1).forEach(g => {
        if (!preloadedRef.current.has(g.id)) {
          preloadedRef.current.add(g.id);
          preloadSprite(g.id);
        }
      });
    }, 500);
    return () => clearTimeout(t);
  }, []);

  const activeGroup = EMOJI_GROUPS.find(g => g.id === activeGroupId) ?? EMOJI_GROUPS[0];
  const spriteReady = useSpriteReady(query ? '' : activeGroupId);

  const filtered = useMemo(() => {
    if (!query) return activeGroup.emojis.map(e => ({ ...e, group: activeGroupId }));
    const q = query.toLowerCase();
    const all = EMOJI_GROUPS.flatMap(g => g.emojis.map(e => ({ ...e, group: g.id })));
    return all.filter(e => e.name.includes(q) || e.keywords.some(k => k.includes(q))).slice(0, 120);
  }, [query, activeGroup, activeGroupId]);

  const handleTabClick = (groupId: string) => {
    setActiveGroupId(groupId);
    setInternalQuery('');
    if (!preloadedRef.current.has(groupId)) {
      preloadedRef.current.add(groupId);
      preloadSprite(groupId);
    }
  };

  return (
    <Flex vertical className={clsx(styles.picker, fillContainer && styles.fillContainer)} role="dialog" aria-label="Emoji picker">
      {/* Tab strip */}
      <div className={styles.tabStrip}>
        {EMOJI_GROUPS.map(group => (
          <Tooltip key={group.id} title={group.label} placement="top">
            <button
              role="tab"
              className={styles.tabBtn}
              data-active={(group.id === activeGroupId && !query) || undefined}
              aria-selected={group.id === activeGroupId && !query}
              onClick={() => handleTabClick(group.id)}
              aria-label={group.label}
            >
              <Icon name={GROUP_ICONS[group.id] ?? 'category'} size={icon.md} />
            </button>
          </Tooltip>
        ))}
      </div>

      {/* Search — hidden when parent provides search externally (e.g. ToolPanel search prop) */}
      {!hideSearch && (
        <div className={styles.search}>
          <Input
            role="searchbox"
            placeholder="Search emojis…"
            value={query}
            onChange={e => setInternalQuery(e.target.value)}
            size="sm"
            prefix={<Icon name="search" size={14} />}
          />
        </div>
      )}

      {/* Grid — skeleton while sprite loads, emojis once ready */}
      {!spriteReady ? (
        <div className={styles.skeleton}>
          {Array.from({ length: 40 }).map((_, i) => (
            <div key={i} className={styles.skeletonCell} />
          ))}
        </div>
      ) : (
        <div className={styles.grid}>
          {filtered.map(emoji => {
            const eid = `${emoji.group}-${emoji.id}`;
            return (
              <IconButton
                key={eid}
                transparent
                aria-label={emoji.name}
                className={styles.emojiBtn}
                data-launching={launchingId === eid || undefined}
                onClick={() => launch(eid, () => onSelect({ group: emoji.group, id: emoji.id, variant }))}
              >
                <EmojiRenderer group={emoji.group} id={emoji.id} size={28} variant={variant} alt={emoji.name} useSprite />
              </IconButton>
            );
          })}
        </div>
      )}

      {showVariantToggle && (
        <Flex className={styles.variantToggle} align="center">
          <Radio.Group
            value={variant}
            onChange={v => setVariant(v as EmojiVariant)}
            options={[
              { label: 'Default', value: 'default' },
              { label: 'Stamp', value: 'stamp' },
            ]}
          />
        </Flex>
      )}
    </Flex>
  );
}
